import { Card, CardContent } from '@design-system/Card';
import { List, ListItem } from '@design-system/List';
import { Link } from '@design-system/Link';
import type { SMPSubscriptionDetail } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  isSubscriptionActiveState,
  formatShippingAddress,
} from '../utils/smpHelpers';
import {
  SMP_CHANGE_ADDRESS_PATH,
  SMP_PAYMENT_METHODS_PATH,
  SMP_DISCOUNT_CODE_PATH,
} from '@/constants/paths';

interface SMPLandingPageShippingPaymentProps {
  subscription: SMPSubscriptionDetail;
}

export const SMPLandingPageShippingPayment = ({
  subscription,
}: SMPLandingPageShippingPaymentProps) => {
  const { subscription_status, shipping_address } = subscription;
  const isActiveState = isSubscriptionActiveState(subscription_status);

  const addressLines = shipping_address
    ? formatShippingAddress(shipping_address)
    : [];
  const hasAddress = addressLines.length > 0;

  const addressContent = (
    <div className="flex flex-col gap-3 min-w-0">
      <span className="typography-body text-primary">Shipping Address</span>
      {hasAddress ? (
        <div className="flex flex-col typography-body text-tertiary break-words">
          {addressLines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
      ) : (
        <span className="typography-body text-tertiary">
          {isActiveState ? 'Tap to add a shipping address' : 'No shipping address on file.'}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="smp-shippingPayment-section">
      <h2
        className="typography-h2 text-primary px-1"
        data-testid="smp-shippingPayment-title"
      >
        Shipping &amp; Payment
      </h2>

      <div className="flex flex-col gap-2" data-testid="smp-shippingPayment-cards">
        {isActiveState ? (
          <Card data-testid="smp-shippingPayment-addressCard">
            <CardContent className="p-0">
              <List>
                <ListItem asChild data-testid="smp-shippingPayment-address">
                  <Link
                    to={SMP_CHANGE_ADDRESS_PATH}
                    className="flex flex-col items-start !h-auto py-5 px-5 !pr-12 !whitespace-normal"
                  >
                    {addressContent}
                  </Link>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="smp-shippingPayment-addressCardCancelled">
            <CardContent className="py-5 px-5">
              {addressContent}
            </CardContent>
          </Card>
        )}

        {isActiveState && (
          <Card data-testid="smp-shippingPayment-paymentMethodsCard">
            <CardContent className="p-0">
              <List>
                <ListItem asChild data-testid="smp-shippingPayment-paymentMethods">
                  <Link to={SMP_PAYMENT_METHODS_PATH}>Payment Methods</Link>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        )}

        {isActiveState && (
          <Card data-testid="smp-shippingPayment-discountCodeCard">
            <CardContent className="p-0">
              <List>
                <ListItem asChild data-testid="smp-shippingPayment-discountCode">
                  <Link to={SMP_DISCOUNT_CODE_PATH}>Discount Code</Link>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
